import { z } from "zod";
import { checkPeriod, CONVERSATION_PERIODS } from "./conversations.js";
import { isoDaySchema } from "./filters.js";
import { freeTextSchema, listOffsetSchema } from "./leads.js";
import { personSchema } from "./people.js";
import { idSchema, multiLineTextSchema, singleLineTextSchema } from "./text.js";
// Le Opportunità (piano M6, R8, Task 24; D23; SEZ-9, SEZ-10): la pagina «Opportunità» con le viste «Per fase» e «Tabella» e il
// riquadro nella scheda del Lead. L'Opportunità nasce e avanza con disco e demo (R7, `appointments.ts`); qui si legge e si chiude
// a mano con «Venduto» o «Persa», e chi guida il team la riapre. Lo status del Lead non cambia mai (SEZ-9).
// - `GET /api/opportunities` (`opportunitiesQuerySchema`): le Opportunità nel perimetro dell'area `opportunities` di chi guarda
//   (`view`: `own` = quelle che segue, `all` = tutte), con fase, «Di chi», campagna, periodo e ricerca, e il conto per fase;
// - `GET /api/opportunities/:id`: una Opportunità con i suoi appuntamenti e che cosa chi guarda può farne;
// - `POST /api/opportunities/:id/sold` (`sellOpportunityBodySchema`), `…/lost` (`loseOpportunityBodySchema`) e `…/reopen`
//   (`reopenOpportunityBodySchema`), con la versione letta (`409 version_conflict`).
/** Le fasi (SEZ-9), nell'ordine delle colonne di «Per fase»: le quattro aperte, poi «Venduto» e «Persa». */
export const OPPORTUNITY_STAGE_VALUES = ["disco_booked", "disco_held", "demo_booked", "demo_held", "sold", "lost"];
/** Le fasi di un'Opportunità aperta: ce n'è al più una per Lead. */
export const OPPORTUNITY_OPEN_STAGE_VALUES = ["disco_booked", "disco_held", "demo_booked", "demo_held"];
/** Che cosa ha comprato (questione 379): «Club» o «Percorso». */
export const OPPORTUNITY_PRODUCTS = ["club", "percorso"];
/**
 * I motivi di «Persa» (questione 380): «Non interessato», «Prezzo», «Tempi», «Concorrente», «Non risponde più», «Altro» con un
 * testo.
 */
export const OPPORTUNITY_LOST_REASONS = ["not_interested", "price", "timing", "competitor", "unreachable", "other"];
/** Il valore di una vendita in centesimi di euro: da 1 centesimo a un milione di euro. */
export const OPPORTUNITY_VALUE_CENTS_MAX = 100_000_000;
/**
 * Query di `GET /api/opportunities`. `stage` è una fase, oppure `open` per le quattro aperte; senza, tutte. `who` («Di chi»: la
 * persona che segue l'Opportunità) vale soltanto per chi le vede tutte (`canFilterByPerson`): per gli altri non allarga e non
 * restringe nulla. `campaign` è il nome della campagna della provenienza con cui l'Opportunità si è aperta. Il periodo (in giorni
 * interi nel fuso dell'azienda, come in «Conversazioni»; di partenza `all`) guarda la chiusura delle vendute e delle perse e
 * l'apertura delle altre. La ricerca segue la regola dell'elenco dei Lead. `stage` restringe le righe, non il conto per fase.
 * `leadId` (Task 25) tiene soltanto le Opportunità di quel Lead, per il riquadro della scheda: l'aperta in cima, poi le chiuse.
 */
export const opportunitiesQuerySchema = z
    .strictObject({
    stage: z.enum([...OPPORTUNITY_STAGE_VALUES, "open"]).optional(),
    who: z.union([z.literal("all"), idSchema]).optional(),
    campaign: freeTextSchema(200).optional(),
    period: z.enum(CONVERSATION_PERIODS).default("all"),
    from: isoDaySchema.optional(),
    to: isoDaySchema.optional(),
    q: freeTextSchema(200).optional(),
    leadId: idSchema.optional(),
    offset: listOffsetSchema,
    limit: z.coerce.number().int().min(1).max(500).default(200),
})
    .superRefine(checkPeriod);
/** L'appuntamento che dice il «Prossimo passo»: l'ultimo per ora di inizio fra quelli non annullati dell'Opportunità. */
export const opportunityAppointmentSchema = z.object({
    id: z.string(),
    kind: z.enum(["disco", "demo"]),
    startsAt: z.string(),
    status: z.enum(["open", "done", "canceled"]),
    result: z.enum(["held", "no_show"]).nullable(),
});
/**
 * Un'Opportunità come la vedono la pagina e il riquadro della scheda: il Lead (nome, salone, status, owner di adesso), la fase e il
 * prossimo passo («Da riprogrammare» dopo un no-show o un annullamento), chi la segue, chi ha prenotato la disco, chi ha chiuso, la
 * campagna della provenienza con cui si è aperta, quando si è aperta e chiusa, i dati della vendita o della perdita, l'appuntamento
 * del prossimo passo e la versione da rimandare con un cambio.
 */
export const opportunityViewSchema = z.object({
    id: z.string(),
    lead: z.object({
        id: z.string(),
        displayName: z.string().nullable(),
        salonName: z.string().nullable(),
        status: z.object({ id: z.string(), name: z.string() }),
        owner: personSchema.nullable(),
    }),
    stage: z.enum(OPPORTUNITY_STAGE_VALUES),
    nextStep: z.literal("reschedule").nullable(),
    assignee: personSchema,
    bookedBy: personSchema.nullable(),
    closedBy: personSchema.nullable(),
    campaign: z.string().nullable(),
    openedAt: z.string(),
    closedAt: z.string().nullable(),
    sale: z
        .object({
        /** `null` per una vendita importata da Close, che non dice Club o Percorso (4/11/2026). */
        product: z.enum(OPPORTUNITY_PRODUCTS).nullable(),
        valueCents: z.number().int(),
        contractType: z.string().nullable(),
        offer: z.string().nullable(),
        paymentStartDate: z.string().nullable(),
        bonus: z.string().nullable(),
    })
        .nullable(),
    lost: z.object({ reason: z.enum(OPPORTUNITY_LOST_REASONS), note: z.string().nullable() }).nullable(),
    appointment: opportunityAppointmentSchema.nullable(),
    version: z.number().int(),
});
/** Il conto per fase: ogni fase, anche a zero. */
export const opportunityStageCountsSchema = z.object({
    disco_booked: z.number().int(),
    disco_held: z.number().int(),
    demo_booked: z.number().int(),
    demo_held: z.number().int(),
    sold: z.number().int(),
    lost: z.number().int(),
});
/**
 * La pagina delle Opportunità: le righe (in ordine di fase; le aperte dal prossimo appuntamento, le chiuse dalla più recente),
 * quante sono con i filtri (`total`), il conto per fase e il valore delle vendute con tutti i filtri tranne la fase, le campagne
 * da scegliere (quelle delle Opportunità nel perimetro e in «Di chi») e se si sceglie «Di chi».
 */
export const opportunitiesResponseSchema = z.object({
    rows: z.array(opportunityViewSchema),
    total: z.number().int(),
    counts: opportunityStageCountsSchema,
    soldValueCents: z.number().int(),
    campaigns: z.array(z.string()),
    canFilterByPerson: z.boolean(),
    offset: z.number().int(),
    limit: z.number().int(),
});
/**
 * `GET /api/opportunities/:id` e la risposta di ogni cambio: l'Opportunità, i suoi appuntamenti che chi guarda vede (dal più
 * recente) e che cosa può farne: «Venduto» e «Persa» (`canUpdate`, su un'Opportunità aperta), «Riapri» (`canReopen`, su una chiusa:
 * soltanto chi cambia tutte le Opportunità, fra i ruoli di partenza Super user, Admin e Super admin).
 */
export const opportunityResponseSchema = z.object({
    opportunity: opportunityViewSchema,
    appointments: z.array(opportunityAppointmentSchema),
    canUpdate: z.boolean(),
    canReopen: z.boolean(),
});
const versionSchema = z.number().int().positive();
/**
 * «Venduto» (questione 379): «Club» o «Percorso», il valore e chi ha chiuso (una persona attiva dell'azienda, a cui conta la
 * vendita) sono obbligatori; tipo di contratto, offerta, data di inizio del pagamento e bonus facoltativi.
 */
export const sellOpportunityBodySchema = z.strictObject({
    product: z.enum(OPPORTUNITY_PRODUCTS),
    valueCents: z.number().int().min(1).max(OPPORTUNITY_VALUE_CENTS_MAX),
    contractType: singleLineTextSchema(100).optional(),
    offer: singleLineTextSchema(200).optional(),
    paymentStartDate: isoDaySchema.optional(),
    bonus: singleLineTextSchema(200).optional(),
    closedByMembershipId: idSchema,
    version: versionSchema,
});
/** «Persa» (questione 380): il motivo; con «Altro» anche il testo. */
export const loseOpportunityBodySchema = z
    .strictObject({ reason: z.enum(OPPORTUNITY_LOST_REASONS), note: multiLineTextSchema(500).optional(), version: versionSchema })
    .refine((body) => body.reason !== "other" || body.note !== undefined, { path: ["note"], message: "«Altro» needs a note" });
/** «Riapri» un'Opportunità venduta o persa: torna alla fase a cui l'avevano portata i suoi appuntamenti. */
export const reopenOpportunityBodySchema = z.strictObject({ version: versionSchema });
//# sourceMappingURL=opportunities.js.map